import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bug, ExternalLink, Lightbulb, MessageSquareWarning } from 'lucide-react'

import { ApiError } from '@/api/client'
import { feedbackApi } from '@/api/endpoints'
import type { Feedback, FeedbackStatus } from '@/api/types'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { FEEDBACK_KIND_LABELS, FEEDBACK_STATUS_LABELS } from '@/lib/dictionaries'
import { formatDateTime } from '@/lib/format'
import { issueUrl } from './issue'

const FILTRY: { value: FeedbackStatus | ''; label: string }[] = [
  { value: 'NEW', label: 'Nowe' },
  { value: 'IN_PROGRESS', label: 'W toku' },
  { value: '', label: 'Wszystkie' },
]

const STATUS_TONES: Record<FeedbackStatus, 'warning' | 'neutral' | 'success' | 'error'> = {
  NEW: 'warning',
  IN_PROGRESS: 'neutral',
  DONE: 'success',
  REJECTED: 'error',
}

/**
 * Lista zgłoszeń błędów i sugestii — widok konta serwisowego wykonawcy.
 *
 * Zgłoszenia trafiają najpierw do bazy, a nie od razu na zewnątrz. Gdyby szły
 * wprost pocztą albo do systemu zgłoszeń, awaria tamtej strony znaczyłaby
 * zgłoszenie stracone bez śladu. Stąd ten ekran jest podstawowym miejscem
 * obsługi, a przeniesienie czegokolwiek dalej to świadomy ruch, nie warunek
 * działania.
 */
export function FeedbackPage() {
  const queryClient = useQueryClient()
  const { notifySuccess, notifyError } = useToast()
  const [filtr, setFiltr] = useState<FeedbackStatus | ''>('NEW')

  const query = useQuery({
    queryKey: ['feedback', filtr],
    queryFn: () => feedbackApi.list({ size: 100, status: filtr || undefined }),
    placeholderData: keepPreviousData,
  })

  const zmienStan = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      feedbackApi.update(id, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feedback'] })
      notifySuccess('Zapisano stan zgłoszenia.')
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się zapisać zmiany.')
    },
  })

  const zgloszenia = query.data?.items ?? []

  return (
    <>
      <PageHeader
        title="Zgłoszenia"
        description="Błędy i sugestie wysłane przez pracowników szkoły."
        breadcrumb={['Szkoła', 'Zgłoszenia']}
      />

      <div className="card mb-6 flex flex-wrap items-center gap-3 p-4">
        <div
          className="flex gap-1 rounded-full bg-surface-container-low p-1"
          role="tablist"
          aria-label="Filtr zgłoszeń"
        >
          {FILTRY.map((item) => (
            <button
              key={item.value || 'wszystkie'}
              type="button"
              role="tab"
              aria-selected={filtr === item.value}
              onClick={() => setFiltr(item.value)}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-bold transition-colors',
                filtr === item.value
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:text-primary',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać zgłoszeń. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      {query.isPending ? (
        <Card>
          <SkeletonRows rows={4} />
        </Card>
      ) : zgloszenia.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageSquareWarning size={28} />}
            title="Brak zgłoszeń"
            description={
              filtr === 'NEW'
                ? 'Nic nowego nie czeka na obsługę.'
                : 'W tym filtrze nie ma żadnych zgłoszeń.'
            }
          />
        </Card>
      ) : (
        <ul className="space-y-4">
          {zgloszenia.map((item) => (
            <li key={item.id}>
              <Zgloszenie
                zgloszenie={item}
                onStatus={(status) => zmienStan.mutate({ id: item.id, status })}
                zapisuje={zmienStan.isPending}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function Zgloszenie({
  zgloszenie,
  onStatus,
  zapisuje,
}: {
  zgloszenie: Feedback
  onStatus: (status: FeedbackStatus) => void
  zapisuje: boolean
}) {
  const Ikona = zgloszenie.kind === 'BUG' ? Bug : Lightbulb

  return (
    <Card>
      <div className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-headline text-sm font-bold text-secondary">
              <Ikona size={16} aria-hidden="true" />
              {FEEDBACK_KIND_LABELS[zgloszenie.kind]}
            </p>
            <p className="mt-1 text-xs text-on-surface-variant">
              {zgloszenie.reporter_name} · {formatDateTime(zgloszenie.created_at)}
            </p>
          </div>
          <Badge tone={STATUS_TONES[zgloszenie.status]}>
            {FEEDBACK_STATUS_LABELS[zgloszenie.status]}
          </Badge>
        </div>

        <p className="whitespace-pre-wrap text-sm leading-relaxed text-on-surface">
          {zgloszenie.message}
        </p>

        <dl className="grid gap-1.5 rounded-lg bg-surface-container-low p-3 text-xs text-on-surface-variant sm:grid-cols-2">
          <Szczegol etykieta="Ekran" wartosc={zgloszenie.page_url} />
          <Szczegol etykieta="Wersja" wartosc={zgloszenie.app_version} />
          <Szczegol etykieta="Okno" wartosc={zgloszenie.viewport} />
          <Szczegol etykieta="Przeglądarka" wartosc={zgloszenie.user_agent} />
        </dl>

        {zgloszenie.console_errors && (
          <details className="rounded-lg bg-surface-container-low p-3">
            <summary className="cursor-pointer text-xs font-bold text-on-surface-variant">
              Błędy techniczne z przeglądarki
            </summary>
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed text-on-surface-variant">
              {zgloszenie.console_errors}
            </pre>
          </details>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-outline/10 pt-4">
          {(['NEW', 'IN_PROGRESS', 'DONE', 'REJECTED'] as FeedbackStatus[])
            .filter((status) => status !== zgloszenie.status)
            .map((status) => (
              <Button
                key={status}
                variant="outline"
                size="sm"
                disabled={zapisuje}
                onClick={() => onStatus(status)}
              >
                {FEEDBACK_STATUS_LABELS[status]}
              </Button>
            ))}

          {/* Przeniesienie do systemu zgłoszeń bez tokenu na serwerze:
              adres jest wypełniony, a issue zakłada się w przeglądarce, w której
              i tak jesteś zalogowany. Nic nie wygasa i nic nie może paść. */}
          <a
            href={issueUrl(zgloszenie)}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-bold text-primary transition-colors hover:bg-primary/10"
          >
            <ExternalLink size={15} aria-hidden="true" />
            Otwórz jako issue
          </a>
        </div>

        {zgloszenie.resolved_by && (
          <p className="text-xs text-on-surface-variant">
            Zamknięte przez {zgloszenie.resolved_by.full_name}
            {zgloszenie.resolved_at ? ` · ${formatDateTime(zgloszenie.resolved_at)}` : ''}
          </p>
        )}
      </div>
    </Card>
  )
}

function Szczegol({ etykieta, wartosc }: { etykieta: string; wartosc: string | null }) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-2">
      <dt className="font-semibold">{etykieta}</dt>
      <dd className="min-w-0 break-words">{wartosc ?? '—'}</dd>
    </div>
  )
}
